import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, ArrowRight } from 'lucide-react';
import publications, { publicationTypes } from '../data/publicationsData';
import SEO from '../components/SEO';

const Publications = () => {
  const [activeType, setActiveType] = useState('All');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = activeType === 'All'
    ? publications
    : publications.filter(p => p.type === activeType);

  const featured = filtered.filter(p => p.featured);
  const rest = filtered.filter(p => !p.featured);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="publications-page">
      <SEO title="Publications & Resources" description="Download free guides, whitepapers, and legal resources on trademark registration, copyright law, and IP protection from Trademark Finity." path="/resources/publications" />
      {/* Hero */}
      <section className="relative bg-primary overflow-hidden text-white py-12 md:py-20">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-block bg-secondary/20 border border-secondary/30 rounded-full px-4 py-2 mb-5 md:mb-6">
            <span className="text-xs sm:text-sm font-semibold text-secondary-100">Free Resources</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 md:mb-6 tracking-tight leading-tight">
            Publications &
            <span className="text-secondary"> Resources</span>
          </h1>
          <p className="text-sm sm:text-lg md:text-xl text-secondary-100 max-w-3xl mx-auto">
            Download our comprehensive guides, whitepapers, and eBooks to master trademark protection
          </p>
        </div>
      </section>

      {/* Type Filters */}
      <section className="bg-white border-b border-slate-200 sticky top-[72px] z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto gap-2 py-4 justify-center scrollbar-hide" data-testid="pub-type-filters">
            {publicationTypes.map((type) => (
              <button
                key={type}
                onClick={() => setActiveType(type)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
                  activeType === type
                    ? 'bg-secondary text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Publications */}
      {featured.length > 0 && (
        <section className="py-10 md:py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-primary mb-6 md:mb-8">Featured Publications</h2>
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {featured.map((pub) => (
                <Link key={pub.id} to={`/resources/publications/${pub.id}`} className="group" data-testid={`pub-featured-${pub.id}`}>
                  <article className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
                    <div className="relative overflow-hidden aspect-[16/9]">
                      <img src={pub.image} alt={pub.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute top-4 left-4">
                        <span className="bg-secondary text-brand text-xs font-bold px-3 py-1.5 rounded-full">{pub.type}</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-lg sm:text-xl font-bold text-primary mb-3 group-hover:text-secondary transition-colors">{pub.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{pub.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1"><FileText className="h-4 w-4" />{pub.pages}</span>
                          <span>{pub.size}</span>
                        </div>
                        <span className="text-secondary text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          View <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Publications */}
      <section className="py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {rest.length > 0 && <h2 className="font-heading text-xl sm:text-2xl font-bold text-primary mb-6 md:mb-8">All Publications</h2>}
          <div className="flex flex-wrap justify-center gap-6 md:gap-8" data-testid="publications-grid">
            {(featured.length === 0 ? filtered : rest).map((pub) => (
              <Link key={pub.id} to={`/resources/publications/${pub.id}`} className="group w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.33%-1.5rem)]" data-testid={`pub-card-${pub.id}`}>
                <article className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img src={pub.image} alt={pub.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 left-4">
                      <span className="bg-secondary text-brand text-xs font-bold px-3 py-1.5 rounded-full">{pub.type}</span>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-lg font-bold text-primary mb-3 group-hover:text-secondary transition-colors leading-tight">{pub.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{pub.description}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span>{pub.pages}</span>
                        <span>PDF</span>
                        <span>{pub.size}</span>
                      </div>
                      <span className="text-secondary text-sm font-semibold flex items-center gap-1">
                        <Download className="h-4 w-4" /> Free
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No publications found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Publications;
